"use client";

import React from "react";
import { Text, SectionCard, CardTitle, Section } from "@/src/components";
import { ChartProvider } from "@/src/components/charts/chart-provider";
import { PieChart } from "@/src/components/charts/pie-chart";

const departmentExpenses = {
  items: [
    { name: "Engineering", value: 35000 },
    { name: "Sales & Marketing", value: 22000 },
    { name: "Operations", value: 18000 },
    { name: "HR & Admin", value: 12000 },
    { name: "Finance", value: 8000 },
  ],
};

const simpleData = {
  items: [
    { name: "Desktop", value: 4200 },
    { name: "Mobile", value: 3800 },
    { name: "Tablet", value: 2100 },
  ],
};

const roseChartData = {
  items: [
    { name: "Category A", value: 85 },
    { name: "Category B", value: 120 },
    { name: "Category C", value: 75 },
    { name: "Category D", value: 110 },
  ],
};

export default function DocsPieChartPage() {
  return (
    <ChartProvider>
      <div className="space-y-theme-5 overflow-auto h-full py-theme-6 px-theme-6">
        {/* Header */}
        <Section>
          <div className="space-y-2">
            <Text as="h1" className="text-2xl font-bold tracking-tight">
              Pie Chart
            </Text>
            <Text className="text-base text-muted-foreground max-w-3xl">
              Display part-to-whole relationships with proportional slices.
              Perfect for expense breakdown, market share, and composition analysis.
            </Text>
          </div>
        </Section>

        {/* Basic Example */}
        <Section title="Basic Example">
          <SectionCard
            header={<CardTitle>Department Expense Breakdown</CardTitle>}
            contentClassName="p-theme-5"
          >
            <PieChart
              data={departmentExpenses}
              height={350}
              showLabels={true}
              showPercentage={true}
            />
          </SectionCard>
        </Section>

        {/* Variants */}
        <Section title="Variants">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-theme-4">
            <SectionCard
              header={<CardTitle>Without Labels</CardTitle>}
              contentClassName="p-theme-5"
            >
              <PieChart
                data={simpleData}
                height={300}
                showLabels={false}
                showPercentage={true}
              />
            </SectionCard>

            <SectionCard
              header={<CardTitle>Nightingale Rose Chart</CardTitle>}
              contentClassName="p-theme-5"
            >
              <PieChart
                data={roseChartData}
                height={300}
                roseType={true}
                showLabels={true}
              />
            </SectionCard>
          </div>
        </Section>

        {/* Only Percentages */}
        <Section title="Percentage Only">
          <SectionCard
            header={<CardTitle>Show Percentages Only</CardTitle>}
            contentClassName="p-theme-5"
          >
            <PieChart
              data={departmentExpenses}
              height={350}
              showLabels={true}
              showPercentage={true}
            />
          </SectionCard>
        </Section>

        {/* Props Table */}
        <Section title="Props">
          <SectionCard
            header={<CardTitle>PieChartProps</CardTitle>}
            contentClassName="p-theme-5 overflow-x-auto"
          >
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-2 font-semibold">Property</th>
                  <th className="text-left py-2 px-2 font-semibold">Type</th>
                  <th className="text-left py-2 px-2 font-semibold">Default</th>
                  <th className="text-left py-2 px-2 font-semibold">Description</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">data</td>
                  <td className="py-2 px-2">PieChartData</td>
                  <td className="py-2 px-2">Required</td>
                  <td className="py-2 px-2">Array of items with name and value</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">title</td>
                  <td className="py-2 px-2">string</td>
                  <td className="py-2 px-2">—</td>
                  <td className="py-2 px-2">Chart title</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">subtitle</td>
                  <td className="py-2 px-2">string</td>
                  <td className="py-2 px-2">—</td>
                  <td className="py-2 px-2">Chart subtitle</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">height</td>
                  <td className="py-2 px-2">string | number</td>
                  <td className="py-2 px-2">100%</td>
                  <td className="py-2 px-2">Chart height in px or CSS value</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">showLegend</td>
                  <td className="py-2 px-2">boolean</td>
                  <td className="py-2 px-2">true</td>
                  <td className="py-2 px-2">Show legend</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">showLabels</td>
                  <td className="py-2 px-2">boolean</td>
                  <td className="py-2 px-2">true</td>
                  <td className="py-2 px-2">Show labels on slices</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">showPercentage</td>
                  <td className="py-2 px-2">boolean</td>
                  <td className="py-2 px-2">true</td>
                  <td className="py-2 px-2">Show percentage in labels</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">roseType</td>
                  <td className="py-2 px-2">boolean</td>
                  <td className="py-2 px-2">false</td>
                  <td className="py-2 px-2">Nightingale rose chart (radius varies)</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">className</td>
                  <td className="py-2 px-2">string</td>
                  <td className="py-2 px-2">—</td>
                  <td className="py-2 px-2">Additional CSS classes</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">loading</td>
                  <td className="py-2 px-2">boolean</td>
                  <td className="py-2 px-2">false</td>
                  <td className="py-2 px-2">Show loading state</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">empty</td>
                  <td className="py-2 px-2">boolean</td>
                  <td className="py-2 px-2">false</td>
                  <td className="py-2 px-2">Show empty state</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">formatValue</td>
                  <td className="py-2 px-2">(value: number) =&gt; string</td>
                  <td className="py-2 px-2">—</td>
                  <td className="py-2 px-2">Format values in tooltip</td>
                </tr>
                <tr className="hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">onSliceClick</td>
                  <td className="py-2 px-2">(params) =&gt; void</td>
                  <td className="py-2 px-2">—</td>
                  <td className="py-2 px-2">Click event handler for slices</td>
                </tr>
              </tbody>
            </table>
          </SectionCard>
        </Section>

        {/* Data Structure */}
        <Section title="Data Structure">
          <SectionCard
            header={<CardTitle>PieChartData Interface</CardTitle>}
            contentClassName="p-theme-5"
          >
            <pre className="bg-muted p-theme-3 rounded-lg text-xs overflow-x-auto">
              <code>{`interface PieChartData {
  items: {
    name: string;           // Slice label / legend name
    value: number;          // Numeric value for slice size
    color?: string;         // Optional custom color (hex/CSS)
  }[];
}

// Example:
const data = {
  items: [
    {
      name: "Engineering",
      value: 35000,
      color: "#3b82f6"
    },
    {
      name: "Sales & Marketing",
      value: 22000
    },
    {
      name: "Operations",
      value: 18000
    }
  ]
};`}</code>
            </pre>
          </SectionCard>
        </Section>
      </div>
    </ChartProvider>
  );
}

export { DocsPieChartPage as PieChartPage };
